import React, { useCallback, useEffect, useSyncExternalStore } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useTelephony } from "../../context/TelephonyContext";
import {
    AUTOMATISCH,
    audioGeraete,
    echteGeraete,
    type AudioGeraet,
    type GeraeteRolle,
} from "../../services/audioGeraete";
import { headset } from "../../services/headsetVerbindung";
import { I } from "../redesign/kvpIcons";
import "../redesign/contactRedesign.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Telefon-Geräte (12.10.2026: „Einstellung für Telefon … Gerät aussuchen für
 * Mikro und Lautsprecher, und Headset nochmal; die Geräteauswahl auch beim
 * Telefonieren anzeigen").
 *
 * `TelefonGeraeteWahl` ist der Inhalt — Mikrofon, Lautsprecher, Klingelton
 * und Headset. Er steht im Fenster „Telefon-Einstellungen" (Knopf neben
 * „Headset" in der Schnellwahl) und kompakt im laufenden Anruf.
 */

function useAudioGeraete() {
    const abonnieren = useCallback((fn: () => void) => audioGeraete.abonnieren(fn), []);
    const status = useSyncExternalStore(abonnieren, () => audioGeraete.getStatus());
    // Beim Öffnen frisch lesen: gerade angesteckte Geräte erscheinen sofort.
    useEffect(() => {
        audioGeraete.starten();
        void audioGeraete.aktualisieren();
    }, []);
    return status;
}

const GeraetZeile: React.FC<{
    rolle: GeraeteRolle;
    titel: string;
    geraete: AudioGeraet[];
    automatischText: string;
}> = ({ rolle, titel, geraete, automatischText }) => {
    const status = useAudioGeraete();
    const r = status.rollen[rolle];
    return (
        <label className="tg-zeile">
            <span className="tg-titel">{titel}</span>
            <select
                className="tg-wahl"
                value={r.einstellung}
                onChange={(e) => audioGeraete.setzeEinstellung(rolle, e.target.value)}
            >
                <option value={AUTOMATISCH}>{automatischText}</option>
                {echteGeraete(geraete).map((g) => (
                    <option key={g.deviceId} value={g.deviceId}>
                        {g.label}
                    </option>
                ))}
            </select>
            {r.einstellung === AUTOMATISCH && <span className="tg-ziel">jetzt: {r.zielName}</span>}
        </label>
    );
};

const HeadsetZeile: React.FC = () => {
    const { headset: status, headsetVerbinden } = useTelephony();
    if (!status.unterstuetzt) return null;
    return (
        <div className="tg-zeile">
            <span className="tg-titel">Headset-Tasten</span>
            <label className="tg-schalter">
                <input
                    type="checkbox"
                    checked={status.aktiviert}
                    onChange={(e) => headset.setzeAktiviert(e.target.checked)}
                />
                In KMVS benutzen (Abheben, Auflegen, Stumm, Lampen)
            </label>
            {status.aktiviert ? (
                <div className="tg-headset">
                    {status.geraet ? (
                        <span
                            className="sw-headset-ok"
                            title="Abheben nimmt an, Einlegen legt auf, die Stummtaste schaltet stumm."
                        >
                            Verbunden: {status.geraet}
                        </span>
                    ) : (
                        <span className="tg-ziel">Kein Headset verbunden</span>
                    )}
                    <button type="button" className="sw-headset-btn" onClick={headsetVerbinden}>
                        {status.geraet ? "Anderes Headset verbinden" : "Headset verbinden"}
                    </button>
                </div>
            ) : null}
            <span className="tg-ziel">
                Telefonierst du auch mit der Linkus-App? Dann hier ausschalten — sonst können sich App und KMVS
                am Headset gegenseitig das Gespräch beenden. Der Ton läuft trotzdem über das Headset.
            </span>
            {status.fehler && <span className="sw-headset-fehler">{status.fehler}</span>}
        </div>
    );
};

export const TelefonGeraeteWahl: React.FC<{ kompakt?: boolean }> = ({ kompakt = false }) => {
    const status = useAudioGeraete();
    return (
        <div className={`tg-wahlen${kompakt ? " kompakt" : ""}`}>
            {status.namenFehlen && (
                <div className="tg-hinweis">
                    Der Browser zeigt die Gerätenamen erst, wenn KMVS das Mikrofon benutzen darf.
                    <button type="button" className="sw-headset-btn" onClick={() => void audioGeraete.namenFreigeben()}>
                        Geräte anzeigen
                    </button>
                </div>
            )}
            <GeraetZeile
                rolle="mikrofon"
                titel="Mikrofon"
                geraete={status.mikrofone}
                automatischText="Automatisch — Headset, sonst Standard"
            />
            {status.ausgabeWaehlbar && (
                <GeraetZeile
                    rolle="lautsprecher"
                    titel="Lautsprecher (Gespräch)"
                    geraete={status.lautsprecher}
                    automatischText="Automatisch — Headset, sonst Standard"
                />
            )}
            {!kompakt && status.klingelWaehlbar && (
                <GeraetZeile
                    rolle="klingel"
                    titel="Klingelton über"
                    geraete={status.lautsprecher}
                    automatischText="Automatisch — Headset, sonst Lautsprecher"
                />
            )}
            <HeadsetZeile />
        </div>
    );
};

const ZahnradIcon = (
    <svg
        width={18}
        height={18}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <circle cx="10" cy="10" r="2.6" />
        <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" />
    </svg>
);

/** Knopf „Einstellungen" neben „Headset" — öffnet das Fenster Telefon-Einstellungen. */
export const TelefonEinstellungenKnopf: React.FC = () => {
    const [offen, setOffen] = React.useState(false);
    return (
        <>
            <button type="button" className="sw-headset-btn tg-knopf" onClick={() => setOffen(true)}>
                {ZahnradIcon} Einstellungen
            </button>
            {offen && <TelefonEinstellungenFenster onClose={() => setOffen(false)} />}
        </>
    );
};

const TelefonEinstellungenFenster: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            // Nur dieses Fenster schließen, nicht die Schnellwahl darunter.
            e.preventDefault();
            e.stopImmediatePropagation();
            onClose();
        };
        alleFenster.addEventListener("keydown", onKey, true);
        return () => alleFenster.removeEventListener("keydown", onKey, true);
    }, [onClose]);

    return createPortal(
        <div className="kvp-rd sw-backdrop tg-backdrop" role="presentation" onPointerDown={onClose}>
            <div
                className="sw-modal tg-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Telefon-Einstellungen"
                onPointerDown={(e) => e.stopPropagation()}
            >
                <div className="sw-head">
                    <span className="sw-head-ico" aria-hidden>
                        {I.phone}
                    </span>
                    <span className="sw-head-title">Telefon-Einstellungen</span>
                    <button type="button" className="sw-close" onClick={onClose} aria-label="Schließen" title="Schließen">
                        <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
                            <path d="M5 5l10 10M15 5L5 15" />
                        </svg>
                    </button>
                </div>
                <div className="tg-body">
                    <div className="tg-abschnitt">Audio</div>
                    <TelefonGeraeteWahl />
                    <p className="tg-fuss">
                        Gilt für diesen Rechner und Browser. „Automatisch" nimmt das Headset, sobald es
                        angesteckt ist.
                    </p>
                </div>
            </div>
        </div>,
        document.body,
    );
};
